import { Editor, Range } from "@tiptap/core";
import {
  AlignmentCenterIcon,
  AlignmentJustifyIcon,
  AlignmentRightIcon,
  AllignmentLeftIcon,
  AttachIcon,
  BlockQuotesIcon,
  BoldIcon,
  BulletPointsIcon,
  ChecklistIcon,
  CodeIcon,
  GridIcon,
  H1Icon,
  H2Icon,
  H3Icon,
  InfoIcon,
  ItalicIcon,
  LinkIcon,
  LinkV2Icon,
  NumberListIcon,
  StrikethroughIcon,
  TextIcon,
  TextV2Icon,
  UnderlineIcon,
} from "components/icons";
import { PdfIcon } from "components/icons/PdfIcon";
import { HighlightPopover } from "../text-highlight";

import { LinkPopover } from "../link-card/LinkPopover";
import { TurnintoDropdown } from "../turninto/TurnintoDropdown";
import { TurnIntoIcon } from "../turninto/TurnIntoIcon";
import {
  allowedMimeTypes,
  handleImageUpload,
} from "../upload/ImageUploadExtension";
import {
  uploadImage,
  generatePlaceholderId,
  findAndReplaceImagePlaceholder,
} from "../upload/uploadUtils";
type SlashCommandProps = {
  editor: Editor;
  range: Range;
};

export interface SlashCommandItem {
  title: string;
  description: string;
  icon: any;
  isActive: (editor: any) => boolean;
  command: (editor: any) => void;
  // keywords: string[];
}

export interface SlashCommandGroup {
  title: string;
  items: SlashCommandItem[];
}

export interface BubbleCommandItem extends SlashCommandItem {
  hasDropdown?: boolean;
  dropdownComponent?:
    | React.ReactNode
    | ((editor: Editor, closePopover?: () => void) => React.ReactNode);
}
export interface BubbleCommandGroup {
  title: string;
  items: BubbleCommandItem[];
}

export const bubbleCommandList: BubbleCommandGroup[] = [
  {
    title: "Turn into",
    items: [
      {
        title: "Turn into",
        description: "Convert to different block type",
        icon: (editor: Editor) => <TurnIntoIcon editor={editor} />,
        isActive: ({ editor }: SlashCommandProps) => false,
        command: ({ editor, range }: SlashCommandProps) => {
          // No direct command, opens dropdown
        },
        hasDropdown: true,
        dropdownComponent: (editor: Editor, closePopover?: () => void) => (
          <TurnintoDropdown editor={editor} onClose={closePopover} />
        ),
      },
    ],
  },
  {
    title: "Formatting",
    items: [
      {
        title: "Bold",
        description: "Make text bold",
        icon: <BoldIcon />,
        isActive: ({ editor }: SlashCommandProps) => editor.isActive("bold"),
        command: ({ editor, range }: SlashCommandProps) => {
          editor.chain().focus().toggleBold().run();
        },
      },
      {
        title: "Italic",
        description: "Make text italic",
        icon: <ItalicIcon />,
        isActive: ({ editor }: SlashCommandProps) => editor.isActive("italic"),
        command: ({ editor, range }: SlashCommandProps) => {
          editor.chain().focus().toggleItalic().run();
        },
      },
      {
        title: "Underline",
        description: "Make text underline",
        icon: <UnderlineIcon />,
        isActive: ({ editor }: SlashCommandProps) =>
          editor.isActive("underline"),
        command: ({ editor, range }: SlashCommandProps) => {
          editor.chain().focus().toggleUnderline().run();
        },
      },
      {
        title: "Strikethrough",
        description: "Make text strikethrough",
        icon: <StrikethroughIcon />,
        isActive: ({ editor }: SlashCommandProps) => editor.isActive("strike"),
        command: ({ editor, range }: SlashCommandProps) => {
          editor.chain().focus().toggleStrike().run();
        },
      },
    ],
  },
  {
    title: "Align",
    items: [
      {
        title: "Align Left",
        description: "Align text to the left",
        icon: <AllignmentLeftIcon />,
        isActive: ({ editor }: SlashCommandProps) =>
          editor.isActive("textAlign", { textAlign: "left" }),
        command: ({ editor, range }: SlashCommandProps) => {
          editor.chain().focus().setTextAlign("left").run();
        },
      },
      {
        title: "Align Center",
        description: "Align text to the center",
        icon: <AlignmentCenterIcon />,
        isActive: ({ editor }: SlashCommandProps) =>
          editor.isActive("textAlign", { textAlign: "center" }),
        command: ({ editor, range }: SlashCommandProps) => {
          editor.chain().focus().setTextAlign("center").run();
        },
      },
      {
        title: "Align Right",
        description: "Align text to the right",
        icon: <AlignmentRightIcon />,
        isActive: ({ editor }: SlashCommandProps) =>
          editor.isActive("textAlign", { textAlign: "right" }),
        command: ({ editor, range }: SlashCommandProps) => {
          editor.chain().focus().setTextAlign("right").run();
        },
      },
      {
        title: "Align Justify",
        description: "Align text to the justify",
        icon: <AlignmentJustifyIcon />,
        isActive: ({ editor }: SlashCommandProps) =>
          editor.isActive("textAlign", { textAlign: "justify" }),
        command: ({ editor, range }: SlashCommandProps) => {
          editor.chain().focus().setTextAlign("justify").run();
        },
      },
    ],
  },
  {
    title: "Text",
    items: [
      {
        title: "Text",
        description: "Make text bold",
        icon: <TextV2Icon />,
        isActive: ({ editor }: SlashCommandProps) => editor.isActive("bold"),
        command: ({ editor, range }: SlashCommandProps) => {
          editor.chain().focus().toggleBold().run();
        },
        hasDropdown: true,
        dropdownComponent: (editor: Editor, closePopover?: () => void) => (
          <HighlightPopover editor={editor} onClose={closePopover} />
        ),
      },
    ],
  },
  {
    title: "Link",
    items: [
      {
        title: "Link",
        description: "Make text bold",
        icon: <LinkV2Icon fontSize="small" />,
        isActive: ({ editor }: SlashCommandProps) => editor.isActive("bold"),
        command: ({ editor, range }: SlashCommandProps) => {
          editor.chain().focus().toggleBold().run();
        },
        hasDropdown: true,
        dropdownComponent: (editor: Editor, closePopover?: () => void) => (
          <LinkPopover editor={editor} onClose={closePopover} />
        ),
      },
    ],
  },
];

export const fixedSlashCommands: SlashCommandItem[] = [
  {
    title: "Bold",
    description: "Make text bold",
    icon: <BoldIcon />,
    isActive: ({ editor }: SlashCommandProps) => editor.isActive("bold"),
    command: ({ editor, range }: SlashCommandProps) => {
      editor.chain().focus().deleteRange(range).toggleBold().run();
    },
  },
  {
    title: "Italic",
    description: "Make text italic",
    icon: <ItalicIcon />,
    isActive: ({ editor }: SlashCommandProps) => editor.isActive("italic"),
    command: ({ editor, range }: SlashCommandProps) => {
      editor.chain().focus().deleteRange(range).toggleItalic().run();
    },
  },
  {
    title: "Underline",
    description: "Make text underline",
    icon: <UnderlineIcon />,
    isActive: ({ editor }: SlashCommandProps) => editor.isActive("underline"),
    command: ({ editor, range }: SlashCommandProps) => {
      editor.chain().focus().deleteRange(range).toggleUnderline().run();
    },
  },
  {
    title: "Strikethrough",
    description: "Make text strikethrough",
    icon: <StrikethroughIcon />,
    isActive: ({ editor }: SlashCommandProps) => editor.isActive("strike"),
    command: ({ editor, range }: SlashCommandProps) => {
      editor.chain().focus().deleteRange(range).toggleStrike().run();
    },
  },
  {
    title: "Align Left",
    description: "Align text to the left",
    icon: <AllignmentLeftIcon />,
    isActive: ({ editor }: SlashCommandProps) =>
      editor.isActive("textAlign", { textAlign: "left" }),
    command: ({ editor, range }: SlashCommandProps) => {
      editor.chain().focus().deleteRange(range).setTextAlign("left").run();
    },
  },
  {
    title: "Align Center",
    description: "Align text to the center",
    icon: <AlignmentCenterIcon />,
    isActive: ({ editor }: SlashCommandProps) =>
      editor.isActive("textAlign", { textAlign: "center" }),
    command: ({ editor, range }: SlashCommandProps) => {
      editor.chain().focus().deleteRange(range).setTextAlign("center").run();
    },
  },
  {
    title: "Align Right",
    description: "Align text to the right",
    icon: <AlignmentRightIcon />,
    isActive: ({ editor }: SlashCommandProps) =>
      editor.isActive("textAlign", { textAlign: "right" }),
    command: ({ editor, range }: SlashCommandProps) => {
      editor.chain().focus().deleteRange(range).setTextAlign("right").run();
    },
  },
  {
    title: "Align Justify",
    description: "Align text to the justify",
    icon: <AlignmentJustifyIcon />,
    isActive: ({ editor }: SlashCommandProps) =>
      editor.isActive("textAlign", { textAlign: "justify" }),
    command: ({ editor, range }: SlashCommandProps) => {
      editor.chain().focus().deleteRange(range).setTextAlign("justify").run();
    },
  },
];

export const basicBlocks: SlashCommandItem[] = [
  {
    title: "H1 Heading (24)",
    description: "Big section heading",
    icon: <H1Icon />,
    isActive: ({ editor }: SlashCommandProps) =>
      editor.isActive("heading", { level: 1 }),
    command: ({ editor, range }: SlashCommandProps) => {
      editor.chain().focus().deleteRange(range).setHeading({ level: 1 }).run();
    },
  },
  {
    title: "H2 Heading (20)",
    description: "Medium section heading",
    icon: <H2Icon />,
    isActive: ({ editor }: SlashCommandProps) =>
      editor.isActive("heading", { level: 2 }),
    command: ({ editor, range }: SlashCommandProps) => {
      editor.chain().focus().deleteRange(range).setHeading({ level: 2 }).run();
    },
  },
  {
    title: "H3 Heading (16)",
    description: "Small section heading",
    icon: <H3Icon />,
    isActive: ({ editor }: SlashCommandProps) =>
      editor.isActive("heading", { level: 3 }),
    command: ({ editor, range }: SlashCommandProps) => {
      editor.chain().focus().deleteRange(range).setHeading({ level: 3 }).run();
    },
  },
  {
    title: "Bullet List",
    description: "Create a simple bullet list",
    icon: <BulletPointsIcon />,
    isActive: ({ editor }: SlashCommandProps) => editor.isActive("bulletList"),
    command: ({ editor, range }: SlashCommandProps) => {
      console.log(editor);
      editor.chain().focus().deleteRange(range).toggleBulletList().run();
    },
  },
  {
    title: "Numbered List",
    description: "Create a list with numbering",
    icon: <NumberListIcon />,
    isActive: ({ editor }: SlashCommandProps) => editor.isActive("orderedList"),
    command: ({ editor, range }: SlashCommandProps) => {
      editor.chain().focus().deleteRange(range).toggleOrderedList().run();
    },
  },

  {
    title: "Checklist",
    description: "Create a simple bullet list",
    icon: <ChecklistIcon />,
    isActive: ({ editor }: SlashCommandProps) => editor.isActive("taskList"),
    command: ({ editor, range }: SlashCommandProps) => {
      editor.chain().focus().deleteRange(range).toggleTaskList().run();
    },
  },
  {
    title: "Blockquote",
    description: "Create a list with numbering",
    icon: <BlockQuotesIcon />,
    isActive: ({ editor }: SlashCommandProps) => editor.isActive("blockquote"),
    command: ({ editor, range }: SlashCommandProps) => {
      editor.chain().focus().deleteRange(range).toggleBlockquote().run();
    },
  },
  {
    title: "Code Block",
    description: "Create a code block with syntax highlighting",
    icon: <CodeIcon />,
    isActive: ({ editor }: SlashCommandProps) => editor.isActive("codeBlock"),
    command: ({ editor, range }: SlashCommandProps) => {
      editor.chain().focus().deleteRange(range).toggleCodeBlock().run();
    },
  },
  {
    title: "Table",
    description: "Insert a table for structured data",
    icon: <GridIcon />,
    isActive: ({ editor }: SlashCommandProps) => editor.isActive("table"),
    command: ({ editor, range }: SlashCommandProps) => {
      editor
        .chain()
        .focus()
        .deleteRange(range)
        .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
        .run();
    },
  },
];
export const slashCommands: SlashCommandGroup[] = [
  {
    title: "Basic Blocks",
    items: basicBlocks,
  },

  {
    title: "Media",
    items: [
      {
        title: "Attach Image",
        description: "Upload an image",
        icon: <AttachIcon />,
        isActive: () => false,
        command: ({ editor, range }: SlashCommandProps) => {
          // Create a hidden file input
          const input = document.createElement("input");
          input.type = "file";
          input.accept = allowedMimeTypes.join(",");

          input.onchange = async (e) => {
            const file = (e.target as HTMLInputElement).files?.[0];
            if (!file) return;

            const insertPos = range.from;

            try {
              handleImageUpload(editor, file, insertPos);
            } catch (error) {
              console.error("Image upload error:", error);
            }
          };

          input.click();
        },
      },
      {
        title: "Attach File",
        description: "Upload an image",
        icon: <AttachIcon />,
        isActive: () => false,
        command: ({ editor, range }: SlashCommandProps) => {
          // Create a hidden file input
          const input = document.createElement("input");
          input.type = "file";
          input.accept = allowedMimeTypes.join(",");

          input.onchange = async (e) => {
            const file = (e.target as HTMLInputElement).files?.[0];
            if (!file) return;

            const insertPos = range.from;

            try {
              handleImageUpload(editor, file, insertPos);
            } catch (error) {
              console.error("Image upload error:", error);
            }
          };

          input.click();
        },
      },
      {
        title: "Embed PDF",
        description: "Upload and embed a PDF document",
        icon: <PdfIcon />,
        isActive: () => false,
        command: ({ editor, range }: SlashCommandProps) => {
          const input = document.createElement("input");
          input.type = "file";
          input.accept = "application/pdf";

          input.onchange = async (e) => {
            const file = (e.target as HTMLInputElement).files?.[0];
            if (!file) return;

            // Delete the slash command range first
            editor.chain().focus().deleteRange(range).run();

            const insertPos = range.from;
            const placeholderId = generatePlaceholderId();

            // Insert pdfEmbed placeholder with loading state
            editor
              .chain()
              .insertContentAt(insertPos, {
                type: "pdfEmbed",
                attrs: {
                  src: "",
                  name: file.name,
                  isLoading: true,
                  "data-placeholder-id": placeholderId,
                },
              })
              .focus()
              .run();

            try {
              const result = await uploadImage(file);

              if (result.success && result.url) {
                findAndReplaceImagePlaceholder(
                  editor,
                  placeholderId,
                  result.url,
                );
              } else {
                // Remove placeholder on error
                console.error("PDF upload failed:", result.error);
                // Find and remove the placeholder node
                editor
                  .chain()
                  .focus()
                  .command(({ tr, state }) => {
                    const doc = state.doc;
                    let placeholderPos: number | null = null;

                    doc.descendants((node: any, pos: number) => {
                      if (
                        node.type.name === "pdfEmbed" &&
                        node.attrs["data-placeholder-id"] === placeholderId
                      ) {
                        placeholderPos = pos;
                        return false;
                      }
                    });

                    if (placeholderPos !== null) {
                      tr.delete(placeholderPos, placeholderPos + 1);
                      return true;
                    }
                    return false;
                  })
                  .run();
              }
            } catch (error) {
              console.error("PDF upload error:", error);
            }
          };

          input.click();
        },
      },
    ],
  },
];
